import { db } from './firebase';
import {
  doc,
  getDoc,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';

export interface PubStepClientItem {
  id: string;
  order: number;

  // Keep original for backward compatibility / easy usage
  imageUrl?: string | null;

  // Optimized variants (recommended)
  thumbnailUrl?: string | null;
  mediumImageUrl?: string | null;
  highImageUrl?: string | null;

  // Optional: future click action
  linkUrl?: string | null;
}

export interface PubStepClientConfig {
  enabled: boolean;
  heightDp: number;
  intervalMs: number;
  slideDurationMs: number; // duration of slide transition between banners
  items: PubStepClientItem[];
  updatedAt?: Date;
  storeId?: string | null;
}

type PubStepClientUpdateOptions = {
  storeId?: string | null;
};

const DEFAULT_CONFIG: PubStepClientConfig = {
  enabled: false,
  heightDp: 32,
  intervalMs: 3500,
  slideDurationMs: 1200,
  items: [],
};

function toJsDate(v: unknown): Date | undefined {
  if (v && typeof v === 'object' && v instanceof Timestamp) {
    return v.toDate();
  }
  return undefined;
}

const scopedRef = (storeId: string) => doc(db, 'pub_step_client', storeId);

const buildConfig = (
  data: Record<string, any> | undefined,
  storeId?: string | null
): PubStepClientConfig => {
  if (!data) {
    return { ...DEFAULT_CONFIG, storeId: storeId || null };
  }

  const itemsRaw = Array.isArray(data.items) ? data.items : [];
  const items: PubStepClientItem[] = itemsRaw
    .filter((x: any) => x && typeof x === 'object')
    .map((x: any) => ({
      id: String(x.id ?? ''),
      order: typeof x.order === 'number' ? x.order : 0,
      imageUrl: typeof x.imageUrl === 'string' ? x.imageUrl : null,
      thumbnailUrl: typeof x.thumbnailUrl === 'string' ? x.thumbnailUrl : null,
      mediumImageUrl: typeof x.mediumImageUrl === 'string' ? x.mediumImageUrl : null,
      highImageUrl: typeof x.highImageUrl === 'string' ? x.highImageUrl : null,
      linkUrl: typeof x.linkUrl === 'string' ? x.linkUrl : null,
    }))
    .filter((x: PubStepClientItem) => x.id.trim().length > 0)
    .sort((a: PubStepClientItem, b: PubStepClientItem) => (a.order ?? 0) - (b.order ?? 0));

  return {
    enabled: typeof data.enabled === 'boolean' ? data.enabled : DEFAULT_CONFIG.enabled,
    heightDp: typeof data.heightDp === 'number' ? data.heightDp : DEFAULT_CONFIG.heightDp,
    intervalMs: typeof data.intervalMs === 'number' ? data.intervalMs : DEFAULT_CONFIG.intervalMs,
    slideDurationMs:
      typeof data.slideDurationMs === 'number'
        ? data.slideDurationMs
        : DEFAULT_CONFIG.slideDurationMs,
    items,
    updatedAt: toJsDate(data.updatedAt),
    storeId: storeId || (typeof data.storeId === 'string' ? data.storeId : null),
  };
};

export async function getPubStepClientConfig(
  storeId?: string | null
): Promise<PubStepClientConfig> {
  if (storeId) {
    const scopedSnap = await getDoc(scopedRef(storeId));
    if (scopedSnap.exists()) {
      return buildConfig(scopedSnap.data() as Record<string, any>, storeId);
    }
  }

  return { ...DEFAULT_CONFIG, storeId: storeId || null };
}

export async function updatePubStepClientConfig(
  partial: Partial<PubStepClientConfig>,
  options: PubStepClientUpdateOptions = {}
): Promise<PubStepClientConfig> {
  const storeId = options.storeId || null;
  const sanitized: any = { ...partial };

  if (!storeId) {
    throw new Error('Aucun magasin actif sélectionné pour enregistrer Pub Step Client.');
  }

  if (sanitized.heightDp !== undefined) {
    const n = Number(sanitized.heightDp);
    if (!Number.isNaN(n)) sanitized.heightDp = n;
  }
  if (sanitized.intervalMs !== undefined) {
    const n = Number(sanitized.intervalMs);
    if (!Number.isNaN(n)) sanitized.intervalMs = n;
  }
  if (sanitized.slideDurationMs !== undefined) {
    const n = Number(sanitized.slideDurationMs);
    if (!Number.isNaN(n)) sanitized.slideDurationMs = n;
  }

  // Normalize items if present
  if (sanitized.items !== undefined) {
    if (!Array.isArray(sanitized.items)) {
      sanitized.items = [];
    } else {
      sanitized.items = (sanitized.items as any[])
        .filter((x) => x && typeof x === 'object')
        .map((x) => ({
          id: String((x as any).id ?? ''),
          order: typeof (x as any).order === 'number' ? (x as any).order : 0,
          imageUrl: typeof (x as any).imageUrl === 'string' ? (x as any).imageUrl : null,
          thumbnailUrl: typeof (x as any).thumbnailUrl === 'string' ? (x as any).thumbnailUrl : null,
          mediumImageUrl: typeof (x as any).mediumImageUrl === 'string' ? (x as any).mediumImageUrl : null,
          highImageUrl: typeof (x as any).highImageUrl === 'string' ? (x as any).highImageUrl : null,
          linkUrl: typeof (x as any).linkUrl === 'string' ? (x as any).linkUrl : null,
        }))
        .filter((x) => (x.id as string).trim().length > 0);
    }
  }

  const batch = writeBatch(db);
  const primaryRef = scopedRef(storeId);

  batch.set(
    primaryRef,
    {
      ...sanitized,
      storeId,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  await batch.commit();

  return await getPubStepClientConfig(storeId);
}
